import { payments } from '@square/web-sdk';
import { fetchWithRedirectGuard } from './http.js';

// Required card-form fields for a "complete" state. postalCode is optional —
// we collect it from the billing address, not the card form.
const REQUIRED_FIELDS = ['cardNumber', 'expirationDate', 'cvv'];

// Square's CardInputEvent fires on each of these as the user interacts with
// the iframe. Every one of them carries detail.currentState.isCompletelyValid
// for the affected field, which is the source of truth — we don't need to
// chase errorClass add/remove pairs.
const CARD_EVENTS = [
  'errorClassAdded',
  'errorClassRemoved',
  'focusClassAdded',
  'focusClassRemoved',
  'cardBrandChanged',
  'postalCodeChanged',
];

let cardInstance      = null;
let activeBilling     = null;
let submitWired       = false;
const validFields     = new Set();

// Called by checkout-payment.js once the customer has confirmed a billing
// address. (Re-)mounts Square's card form into `.card-container` and wires
// the Place Order button to tokenise + submit on click.
//
// Expects an `address-form:confirm` detail-shaped object:
//   { firstName, lastName, addressLine1, addressLine2, addressLine3, city, postcode }
export async function showCardCapture(billing) {
  activeBilling = billing;
  document.querySelector('.card-details')?.classList.remove('d-none');

  // Re-opening with a different billing address — tear down the previous card
  // form so Square re-renders fresh and we re-track validity from scratch.
  if (cardInstance) {
    notifyCardComplete(false);
    validFields.clear();
    try { await cardInstance.destroy(); } catch (_) { /* already detached */ }
    cardInstance = null;
  }

  const meta = document.querySelector('meta[name="opilam"]')?.content;
  if (!meta) {
    console.log('No opilam meta tag — Square card capture not configured');
    return;
  }
  const [appId, locationId] = meta.split('|');
  if (!appId || !locationId) {
    console.log('Invalid opilam meta tag — expected APP_ID|LOCATION_ID');
    return;
  }

  let sq;
  try {
    sq = await payments(appId, locationId);
  } catch (err) {
    console.error('Square payments() failed:', err);
    return;
  }

  const style = {
    ".input-container": {
      borderColor: '#E8E8E8',
      borderRadius: '0',
      borderWidth: '1px',
    },
    ".input-container.is-error": {
      borderColor: '#8F1E2A',
      borderWidth: '1px',
    },
    ".input-container.is-focus": {
      borderColor: '#1C1C1C',
      borderWidth: '1px',
    },
    ".message-icon": {
      color: '#1C1C1C'
    },
    ".message-icon.is-error": {
      color: '#8F1E2A'
    },
    ".message-text": {
      color: '#1C1C1C'
    },
    ".message-text.is-error": {
      color: '#8F1E2A'
    },
    "input": {
      fontFamily: 'serif',
      fontSize: '14px',
      color: '#1C1C1C',
    },
    "input.is-error": {
      color: '#8F1E2A'
    }
  }
  // Pre-populate Square's postcode field with the billing postcode so the
  // customer doesn't have to retype it. Square's Web Payments SDK doesn't
  // expose a way to hide the field — postcode is required for AVS — so
  // pre-filling is the cleanest UX.
  cardInstance = await sq.card({
    postalCode: (billing?.postcode || '').trim(),
    style
  });
  CARD_EVENTS.forEach(evt => cardInstance.addEventListener(evt, onCardEvent));
  await cardInstance.attach('.card-container');

  wireSubmitHandler();
}

// Single handler for every CardInputEvent — checks the field's
// currentState.isCompletelyValid (Square's own validation flag) and
// updates the validFields set accordingly. Works whether the field was
// previously errored or filled correctly on the first attempt.
function onCardEvent(e) {
  const field = e.detail?.field;
  if (!field) return;
  const valid = !!e.detail?.currentState?.isCompletelyValid;
  if (valid) validFields.add(field);
  else       validFields.delete(field);
  notifyCardComplete(isCardComplete());
}

function isCardComplete() {
  return REQUIRED_FIELDS.every(f => validFields.has(f));
}

function notifyCardComplete(complete) {
  document.dispatchEvent(new CustomEvent('payment:card-complete', {
    detail: { complete },
  }));
}

function wireSubmitHandler() {
  if (submitWired) return;
  submitWired = true;
  const btn = document.getElementById('place-order-button');
  if (!btn) return;

  btn.addEventListener('click', async (e) => {
    if (btn.disabled || !cardInstance) return;
    e.preventDefault();
    setSubmitting(btn, true);

    const verificationDetails = {
      amount:            btn.dataset.amount   || '0',
      currencyCode:      btn.dataset.currency || 'GBP',
      intent:            'CHARGE',
      customerInitiated: true,
      sellerKeyedIn:     false,
      billingContact:    toBillingContact(activeBilling),
    };

    let result;
    try {
      result = await cardInstance.tokenize(verificationDetails);
    } catch (err) {
      console.error('Card tokenize threw:', err);
      setSubmitting(btn, false);
      return;
    }

    if (result.status !== 'OK') {
      console.error('Card tokenize failed:', result.errors);
      setSubmitting(btn, false);
      return;
    }

    await submitOrder(result.token, btn);
  });
}

function toBillingContact(billing) {
  if (!billing) return undefined;
  const addressLines = [billing.addressLine1, billing.addressLine2, billing.addressLine3]
    .map(s => (s || '').trim())
    .filter(Boolean);
  return {
    givenName:   billing.firstName,
    familyName:  billing.lastName,
    addressLines,
    city:        billing.city,
    postalCode:  billing.postcode,
    countryCode: (billing.country || 'GB').toUpperCase(),
  };
}

async function submitOrder(nonce, btn) {
  const url = btn.dataset.next;
  if (!url) {
    console.error('No data-next URL on place-order-button');
    setSubmitting(btn, false);
    return;
  }

  const csrfToken  = document.querySelector('meta[name="_csrf"]')?.content;
  const csrfHeader = document.querySelector('meta[name="_csrf_header"]')?.content || 'X-CSRF-TOKEN';

  const headers = { 'Content-Type': 'application/json' };
  if (csrfToken) headers[csrfHeader] = csrfToken;

  try {
    const res = await fetchWithRedirectGuard(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ nonce }),
    });
    if (!res.ok) throw new Error(`Order submit failed (${res.status})`);
    const data = await res.json().catch(() => ({}));
    if (data.redirect) {
      window.location.href = data.redirect;
    }
  } catch (err) {
    console.error('Order submit failed:', err);
    setSubmitting(btn, false);
  }
}

// Toggles the Place Order button's submitting state. Disabling prevents
// double-clicks; the `is-loading` class swaps the label for a spinner;
// aria-busy informs assistive tech.
function setSubmitting(btn, submitting) {
  btn.disabled = submitting;
  btn.classList.toggle('is-loading', submitting);
  if (submitting) btn.setAttribute('aria-busy', 'true');
  else            btn.removeAttribute('aria-busy');
}
